// src/components/AssetManager.jsx
import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { getAuth } from 'firebase/auth';
import AssetBuilder from './AssetBuilder';
import { useTranslation } from 'react-i18next';

export default function AssetManager({ companyId }) {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [assetTypes, setAssetTypes] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState('');
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileModalUrl, setFileModalUrl] = useState('');
  const [fileModalName, setFileModalName] = useState('');

  const auth = getAuth();
  const currentUser = auth.currentUser;

  // Load assets
  useEffect(() => {
    if (!companyId) {
      console.log('AssetManager: No companyId provided');
      setLoading(false);
      return;
    }

    console.log('AssetManager: Loading assets for company:', companyId);

    const q = query(
      collection(db, 'companies', companyId, 'assets')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('AssetManager: Received', snapshot.docs.length, 'assets');
      
      const assetList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt client-side (newest first)
      assetList.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      
      setAssets(assetList);

      // Extract unique asset types
      const types = [...new Set(assetList.map(a => a.type).filter(Boolean))];
      setAssetTypes(types);

      setLoading(false);
    }, (error) => {
      console.error('Error loading assets:', error);
      alert(t('assets.failedToLoad') + ': ' + error.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Load folders
  useEffect(() => {
    if (!companyId) return;

    const q = query(collection(db, 'companies', companyId, 'assetFolders'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const folderList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt
      folderList.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return aTime - bTime;
      });
      
      setFolders(folderList);
    });

    return () => unsubscribe();
  }, [companyId]);

  async function handleSaveAsset(assetData) {
    try {
      console.log('Saving asset:', { companyId, assetData });
      
      if (editingAsset) {
        // Update existing asset
        await updateDoc(doc(db, 'companies', companyId, 'assets', editingAsset.id), {
          ...assetData,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid
        });
        console.log('Asset updated successfully');
      } else {
        // Create new asset
        const docRef = await addDoc(collection(db, 'companies', companyId, 'assets'), {
          ...assetData,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid,
          companyId
        });
        console.log('Asset created successfully with ID:', docRef.id);
      }

      setShowBuilder(false);
      setEditingAsset(null);
    } catch (error) {
      console.error('Error saving asset:', error);
      alert('Failed to save asset: ' + error.message);
    }
  }

  async function handleDeleteAsset(assetId) {
    if (!window.confirm(t('assets.confirmDeleteAsset'))) {
      return;
    }

    try {
      const asset = assets.find(a => a.id === assetId);
      
      // Delete main photo if exists
      if (asset?.mainPhotoURL) {
        try {
          const photoRef = ref(storage, asset.mainPhotoURL);
          await deleteObject(photoRef);
        } catch (err) {
          console.warn('Could not delete main photo:', err);
        }
      }

      // Delete file attachments
      if (asset?.fields) {
        for (const field of asset.fields) {
          if (field.type === 'file' && field.value) {
            try {
              const fileRef = ref(storage, field.value);
              await deleteObject(fileRef);
            } catch (err) {
              console.warn('Could not delete file:', err);
            }
          }
        }
      }

      await deleteDoc(doc(db, 'companies', companyId, 'assets', assetId));
    } catch (error) {
      console.error('Error deleting asset:', error);
      alert(t('assets.failedToDelete') + ': ' + error.message);
    }
  }

  function handleEditAsset(asset) {
    setEditingAsset(asset);
    setShowBuilder(true);
  }

  async function createFolder() {
    if (!companyId || !newFolderName.trim()) return;
    try {
      await addDoc(collection(db, 'companies', companyId, 'assetFolders'), {
        name: newFolderName.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewFolderName('');
      setShowCreateFolder(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      alert('❌ ' + t('assets.failedToCreateFolder'));
    }
  }

  async function deleteFolder(folderId) {
    if (!companyId) return;
    
    // Move assets out of folder first
    const assetsInFolder = assets.filter(a => a.folderId === folderId);
    try {
      // Update all assets in folder to remove folderId
      await Promise.all(
        assetsInFolder.map(asset => 
          setDoc(
            doc(db, 'companies', companyId, 'assets', asset.id),
            { folderId: null, updatedAt: serverTimestamp() },
            { merge: true }
          )
        )
      );
      
      // Delete the folder
      await deleteDoc(doc(db, 'companies', companyId, 'assetFolders', folderId));
      
      // Reset selected folder if it was deleted
      if (selectedFolder === folderId) {
        setSelectedFolder(null);
      }
    } catch (err) {
      console.error('Error deleting folder:', err);
      alert('❌ ' + t('assets.failedToDeleteFolder'));
    }
  }

  async function updateFolderName(folderId, newName) {
    if (!companyId || !newName.trim()) return;
    try {
      await setDoc(
        doc(db, 'companies', companyId, 'assetFolders', folderId),
        { name: newName.trim(), updatedAt: serverTimestamp() },
        { merge: true }
      );
      setEditingFolder(null);
    } catch (err) {
      console.error('Error updating folder:', err);
      alert('❌ ' + t('assets.failedToUpdateFolder'));
    }
  }

  async function moveAssetToFolder(assetId, folderId) {
    if (!companyId) return;
    try {
      await setDoc(
        doc(db, 'companies', companyId, 'assets', assetId),
        { folderId: folderId, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error('Error moving asset:', err);
      alert('❌ ' + t('assets.failedToMoveAsset'));
    }
  }

  function handleEditAsset(asset) {
    setEditingAsset(asset);
    setShowBuilder(true);
  }

  function handleCloseBuilder() {
    setShowBuilder(false);
    setEditingAsset(null);
  }

  // Filter assets by folder, search, and type
  const filteredAssets = assets.filter(asset => {
    // Folder filter
    const matchesFolder = selectedFolder === null 
      ? !asset.folderId 
      : asset.folderId === selectedFolder;

    const matchesSearch = 
      asset.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.role?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || asset.type === filterType;

    return matchesFolder && matchesSearch && matchesType;
  });

  // Calculate folder counts
  const folderCounts = {};
  folderCounts.unfiled = assets.filter(a => !a.folderId).length;
  folders.forEach(folder => {
    folderCounts[folder.id] = assets.filter(a => a.folderId === folder.id).length;
  });

  if (showBuilder) {
    return (
      <AssetBuilder
        companyId={companyId}
        asset={editingAsset}
        onSave={handleSaveAsset}
        onCancel={handleCloseBuilder}
      />
    );
  }

  return (
    <div className="h-full flex relative bg-gradient-to-br from-slate-900 via-slate-950 to-rose-900/70 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.06),transparent_30%)] pointer-events-none" />

      {/* Folder Sidebar */}
      <div className="w-64 bg-white/5 border-r border-white/15 p-4 space-y-2 overflow-y-auto backdrop-blur-xl relative z-10 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">{t('assets.folders')}</h3>
          <button
            onClick={() => setShowCreateFolder(true)}
            className="border border-white/25 bg-white/15 hover:bg-white/25 text-white px-2 py-1 rounded text-xs shadow-sm backdrop-blur-md"
            title={t('assets.createNewFolder')}
          >
            ➕
          </button>
        </div>
        
        {/* Unfiled Assets */}
        <div
          onClick={() => setSelectedFolder(null)}
          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors border border-transparent ${
            selectedFolder === null
              ? 'bg-white/15 border-white/25 text-white shadow-sm'
              : 'hover:bg-white/10 text-white/80'
          }`}
        >
          <div className="flex items-center gap-2">
            <span>📦</span>
            <span className="text-sm font-medium">{t('assets.unfiled')}</span>
          </div>
          <span className="text-xs text-white/60">
            {folderCounts.unfiled || 0}
          </span>
        </div>
        
        {/* Folder List */}
        {folders.map(folder => (
          <div key={folder.id} className="group">
            <div
              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors border border-transparent ${
                selectedFolder === folder.id
                  ? 'bg-white/15 border-white/25 text-white shadow-sm'
                  : 'hover:bg-white/10 text-white/80'
              }`}
            >
              <div 
                onClick={() => setSelectedFolder(folder.id)}
                className="flex items-center gap-2 flex-1"
              >
                <span>📁</span>
                {editingFolder === folder.id ? (
                  <input
                    type="text"
                    defaultValue={folder.name}
                    className="text-sm bg-white/10 border border-white/30 rounded px-2 py-1 flex-1 text-white shadow-sm"
                    onBlur={(e) => updateFolderName(folder.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') updateFolderName(folder.id, e.target.value);
                      if (e.key === 'Escape') setEditingFolder(null);
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="text-sm font-medium truncate">{folder.name}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-white/60">
                  {folderCounts[folder.id] || 0}
                </span>
                <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingFolder(folder.id);
                    }}
                    className="text-xs text-white/60 hover:text-white"
                    title={t('assets.renameFolder')}
                  >
                    ✏️
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(t('assets.confirmDeleteFolder', { name: folder.name }))) {
                        deleteFolder(folder.id);
                      }
                    }}
                    className="text-xs text-white/60 hover:text-rose-200"
                    title={t('assets.deleteFolder')}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Create Folder Form */}
        {showCreateFolder && (
          <div className="p-3 border border-white/25 rounded-lg bg-white/10 backdrop-blur-md shadow-sm">
            <input
              type="text"
              placeholder={t('assets.folderNamePlaceholder')}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              className="w-full text-sm bg-white/15 border border-white/25 rounded px-2 py-2 mb-2 text-white placeholder-white/60 shadow-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') createFolder();
                if (e.key === 'Escape') {
                  setShowCreateFolder(false);
                  setNewFolderName('');
                }
              }}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={createFolder}
                className="flex-1 border border-white/25 bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded text-xs shadow-sm"
              >
                {t('assets.create')}
              </button>
              <button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderName('');
                }}
                className="flex-1 border border-white/20 px-3 py-2 rounded text-xs text-white hover:bg-white/10"
              >
                {t('assets.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="bg-white/10 border-b border-white/15 px-6 py-4 backdrop-blur-xl shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {selectedFolder === null ? (
                <>📦 {t('assets.unfiledAssets')}</>
              ) : (
                <>📁 {folders.find(f => f.id === selectedFolder)?.name || t('assets.unknownFolder')}</>
              )}
            </h1>
            <p className="text-sm text-white/70 mt-1">
              {t('assets.assetCount', { count: filteredAssets.length })}
            </p>
          </div>
          <button
            onClick={() => setShowBuilder(true)}
            className="border border-white/25 bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-md shadow-sm font-medium backdrop-blur-md"
          >
            ➕ {t('assets.newAsset')}
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder={t('assets.searchAssets')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-white/25 rounded-md px-3 py-2 bg-white/10 text-white placeholder-white/60 backdrop-blur-md shadow-sm"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-white/25 rounded-md px-3 py-2 bg-white/10 text-white backdrop-blur-md shadow-sm"
          >
            <option value="all">{t('assets.allTypes')}</option>
            {assetTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Asset Grid */}
      <div className="flex-1 overflow-auto p-6 relative z-10">
        {loading ? (
          <div className="text-center py-12 text-white/70">
            {t('assets.loadingAssets')}
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {searchTerm || filterType !== 'all' ? t('assets.noAssetsFound') : t('assets.noAssetsYet')}
            </h3>
            <p className="text-white/80 mb-4">
              {searchTerm || filterType !== 'all' 
                ? t('assets.tryAdjustingFilters')
                : t('assets.createFirstAsset')}
            </p>
            {!searchTerm && filterType === 'all' && (
              <button
                onClick={() => setShowBuilder(true)}
                className="border border-white/25 bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-md shadow-sm backdrop-blur-md"
              >
                ➕ {t('assets.createAsset')}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredAssets.map(asset => (
              <AssetCard
                key={asset.id}
                asset={asset}
                folders={folders}
                onEdit={() => handleEditAsset(asset)}
                onDelete={() => handleDeleteAsset(asset.id)}
                onMoveToFolder={moveAssetToFolder}
                onViewImage={(url) => {
                  setImageModalUrl(url);
                  setImageModalOpen(true);
                }}
                onViewFile={(url, name) => {
                  setFileModalUrl(url);
                  setFileModalName(name);
                  setFileModalOpen(true);
                }}
              />
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Image Viewer Modal */}
      {imageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setImageModalOpen(false)}>
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setImageModalOpen(false)}
              className="absolute top-2 right-2 bg-white dark:bg-gray-800 rounded-full w-10 h-10 flex items-center justify-center text-2xl hover:bg-gray-100 dark:hover:bg-gray-700 z-10"
            >
              ✕
            </button>
            <img
              src={imageModalUrl}
              alt={t('assets.assetPreview')}
              className="max-w-full max-h-[85vh] object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {fileModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setFileModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold dark:text-white">📎 {fileModalName}</h2>
                <button
                  onClick={() => setFileModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-3">
                <a
                  href={fileModalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-center"
                >
                  🔗 {t('assets.openInNewTab')}
                </a>
                <a
                  href={fileModalUrl}
                  download
                  className="block w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded text-center"
                >
                  💾 {t('assets.downloadFile')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AssetCard({ asset, folders, onEdit, onDelete, onMoveToFolder, onViewImage, onViewFile }) {
  const [showMenu, setShowMenu] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="bg-white/10 border border-white/20 rounded-2xl shadow-lg hover:shadow-rose-500/10 transition-shadow backdrop-blur-lg overflow-hidden">
      {/* Main Photo */}
      <div className="relative h-48 bg-gradient-to-br from-slate-800 to-slate-900">
        {asset.mainPhotoURL ? (
          <img
            src={asset.mainPhotoURL}
            alt={asset.title}
            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => onViewImage?.(asset.mainPhotoURL)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl text-white/80">
            {asset.type === 'Person' ? '👤' : 
             asset.type === 'Vehicle' ? '🚗' :
             asset.type === 'Equipment' ? '⚙️' : '📦'}
          </div>
        )}

        {/* Menu button */}
        <div className="absolute top-2 right-2">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="bg-white/80 dark:bg-white/10 text-gray-900 dark:text-white rounded-full p-2 shadow-md hover:bg-white hover:text-gray-900 transition-colors"
          >
            ⋮
          </button>
          {showMenu && (
            <div className="absolute right-0 mt-1 bg-slate-900/90 border border-white/15 rounded-xl shadow-2xl py-1 z-10 min-w-[180px] backdrop-blur-xl text-white">
              <button
                onClick={() => {
                  onEdit();
                  setShowMenu(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-white/10"
              >
                ✏️ {t('assets.edit')}
              </button>
              
              {/* Move to Folder */}
              <div className="px-4 py-2 text-xs text-white/60 border-t border-white/10">
                {t('assets.moveTo')}
              </div>
              <button
                onClick={() => {
                  onMoveToFolder?.(asset.id, null);
                  setShowMenu(false);
                }}
                className={`block w-full text-left px-4 py-2 text-sm hover:bg-white/10 ${
                  !asset.folderId ? 'bg-white/10' : ''
                }`}
              >
                📦 {t('assets.unfiled')}
              </button>
              {folders?.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => {
                    onMoveToFolder?.(asset.id, folder.id);
                    setShowMenu(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-white/10 ${
                    asset.folderId === folder.id ? 'bg-white/10' : ''
                  }`}
                >
                  📁 {folder.name}
                </button>
              ))}
              
              <div className="border-t border-white/10 mt-1 pt-1">
                <button
                  onClick={() => {
                    onDelete();
                    setShowMenu(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-rose-500/20"
                >
                  🗑️ {t('assets.delete')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold text-lg text-white mb-1 truncate">
          {asset.title || t('assets.untitledAsset')}
        </h3>
        
        {asset.type && (
          <div className="inline-block bg-white/15 text-white text-xs px-2 py-1 rounded-full mb-2 border border-white/20">
            {asset.type}
          </div>
        )}

        {asset.role && (
          <p className="text-sm text-white/80 mb-2">
            {asset.role}
          </p>
        )}

        {/* Field count and file attachments */}
        <div className="mt-2 space-y-1">
          {asset.fields && asset.fields.length > 0 && (
            <p className="text-xs text-white/60">
              📋 {t('assets.fieldCount', { count: asset.fields.length })}
            </p>
          )}
          {asset.fields && asset.fields.filter(f => f.type === 'file' && f.value).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {asset.fields.filter(f => f.type === 'file' && f.value).map((field, idx) => (
                <button
                  key={idx}
                  onClick={() => onViewFile?.(field.value, field.label || t('assets.file'))}
                  className="text-xs bg-white/10 border border-white/20 text-white px-2 py-1 rounded hover:bg-white/15"
                >
                  📎 {field.label || t('assets.file')}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
